/**
 * Client-side VisitStart element catalog helpers.
 * Source of truth: firebase/seed/visitStartElements.json (same as server runtime copy).
 *
 * Identity stays core (see functions/src/visitstart/visitStartElements.js).
 * SHA / referral / payment / dependents are manager-configurable for
 * visitstart_admin_governor via the same tciGovernanceProfiles pass as clinical.
 */
import visitStartElementsCatalog from "../../../firebase/seed/visitStartElements.json";

export const VISIT_START_TCI_ID = "visit-start";
export const VISIT_START_ELEMENTS_PASS = "visit-start-elements";

export function getVisitStartElements() {
  return Array.isArray(visitStartElementsCatalog?.elements)
    ? visitStartElementsCatalog.elements
    : [];
}

export function getVisitStartCoreElements() {
  return getVisitStartElements().filter(
    (element) => element?.managerConfigurable === false || element?.tier === "core",
  );
}

export function getVisitStartConfigurableElements() {
  return getVisitStartElements().filter((element) => element?.managerConfigurable === true);
}

export function getVisitStartCoreElementIds() {
  return getVisitStartCoreElements()
    .map((element) => String(element?.elementId || "").trim())
    .filter(Boolean);
}

export function getVisitStartConfigurableElementIds() {
  return getVisitStartConfigurableElements()
    .map((element) => String(element?.elementId || "").trim())
    .filter(Boolean);
}

export const VISIT_START_ELEMENT_CATEGORIES = Object.freeze([
  "administrative",
  "clinical",
]);

/** Injected after identity — not a 15th governed catalog element. */
export const VISIT_START_PHOTO_ID_ELEMENT_ID = "photo-id";

/**
 * Category for a catalog element, or the injected photo-id step.
 * Unknown ids return null rather than a guessed category.
 */
export function resolveVisitStartElementCategory(elementId) {
  const id = String(elementId || "").trim();
  if (!id) return null;
  if (id === VISIT_START_PHOTO_ID_ELEMENT_ID) return "administrative";
  const match = getVisitStartElements().find(
    (element) => String(element?.elementId || "").trim() === id,
  );
  const category = String(match?.category || "").trim();
  return VISIT_START_ELEMENT_CATEGORIES.includes(category) ? category : null;
}

export function getVisitStartElementsByCategory(category) {
  const wanted = String(category || "").trim();
  if (!VISIT_START_ELEMENT_CATEGORIES.includes(wanted)) return [];
  return getVisitStartElements().filter(
    (element) => String(element?.category || "").trim() === wanted,
  );
}

export function filterElementIdsByCategory(elementIds, category) {
  const wanted = String(category || "").trim();
  return (Array.isArray(elementIds) ? elementIds : [])
    .map((id) => String(id || "").trim())
    .filter((id) => id && resolveVisitStartElementCategory(id) === wanted);
}

/**
 * Default enabled set: all core + configurable elements with defaultEnabled true.
 * Matches functions/src/fde/tciGovernanceProfiles.js defaultVisitStartEnabledElements.
 */
export function defaultVisitStartEnabledElements() {
  return getVisitStartElements()
    .filter(
      (element) =>
        element?.tier === "core" ||
        element?.managerConfigurable === false ||
        element?.defaultEnabled === true,
    )
    .map((element) => String(element?.elementId || "").trim())
    .filter(Boolean);
}

/**
 * Build enabledElements from a set of configurable element ids.
 * Core ids are always included — never omit them client-side.
 */
export function buildEnabledElements(configurableIds = []) {
  const coreIds = getVisitStartCoreElementIds();
  const allowedConfigurable = new Set(getVisitStartConfigurableElementIds());
  const selected = [];
  const seen = new Set();

  coreIds.forEach((elementId) => {
    if (!seen.has(elementId)) {
      seen.add(elementId);
      selected.push(elementId);
    }
  });

  (Array.isArray(configurableIds) ? configurableIds : []).forEach((rawId) => {
    const elementId = String(rawId || "").trim();
    if (!elementId || !allowedConfigurable.has(elementId) || seen.has(elementId)) return;
    seen.add(elementId);
    selected.push(elementId);
  });

  return selected;
}

/**
 * Keep only governance entries whose keys are in enabledElements.
 * Required because the server rejects governance for disabled elements, and
 * Firestore merge replaces the whole elementGovernance map.
 */
export function filterElementGovernance(elementGovernance, enabledIds) {
  const enabled = new Set(
    (Array.isArray(enabledIds) ? enabledIds : [])
      .map((id) => String(id || "").trim())
      .filter(Boolean),
  );
  const source =
    elementGovernance && typeof elementGovernance === "object" && !Array.isArray(elementGovernance)
      ? elementGovernance
      : {};
  const filtered = {};
  Object.entries(source).forEach(([rawKey, entry]) => {
    const elementId = String(rawKey || "").trim();
    if (!elementId || !enabled.has(elementId)) return;
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return;
    filtered[elementId] = {
      autonomyPosture: String(entry.autonomyPosture || "balanced").trim() || "balanced",
      mustDo: Array.isArray(entry.mustDo)
        ? entry.mustDo.map((item) => String(item || "").trim()).filter(Boolean)
        : [],
      mustNotDo: Array.isArray(entry.mustNotDo)
        ? entry.mustNotDo.map((item) => String(item || "").trim()).filter(Boolean)
        : [],
    };
  });
  return filtered;
}

export function resolveEnabledElements(profile) {
  if (Array.isArray(profile?.enabledElements) && profile.enabledElements.length) {
    return buildEnabledElements(
      profile.enabledElements.filter((id) =>
        getVisitStartConfigurableElementIds().includes(id),
      ),
    );
  }
  return defaultVisitStartEnabledElements();
}
